import { create } from "zustand";
import { persist } from "zustand/middleware";
import { FieldState } from "./jobPostingStore";

interface TemporaryJobPostingState {
  draftId?: string;
  jobId?: string;
  isDraft?: boolean;
  jobPlan?: string;
  lastSavedAt?: string; // ISO timestamp of when the draft was last saved locally
  localState: FieldState[];
  finalState: FieldState[];
  computeStore: () => void;
  getFld: (id: string, state?: "local" | "final") => FieldState | undefined;
  setValue: (id: string, value: any, state?: "local" | "final") => void;
  setFieldProperty: (
    id: string,
    property: keyof FieldState,
    value: any,
    state?: "local" | "final"
  ) => void;
  hydrateFromData: (data: FieldState[]) => void;
  clearStore: () => void;
  updateLastSavedAt: () => void;
  setDraftId: (draftId: string) => void;
  setJobId: (jobId: string) => void;
  setJobPlan: (jobPlan: string) => void;
  setIsDraft: (isDraft: boolean) => void;
}
export const useTemporaryJobPostingStore = create<TemporaryJobPostingState>()(
  persist(
    (set, get) => ({
      draftId: undefined,
      jobId: undefined,
      jobPlan: undefined,
      lastSavedAt: undefined,
      localState: [],
      finalState: [],
      isDraft: false,
      computeStore: () => {
        const { localState } = get();
        set({ finalState: [...localState] });
      },

      getFld: (id: string, state: "local" | "final" = "local") => {
        const { localState, finalState } = get();
        const targetState = state === "local" ? localState : finalState;
        return targetState.find(field => field.id === id);
      },

      setValue: (
        id: string,
        value: any,
        state: "local" | "final" = "local"
      ) => {
        const { localState, finalState } = get();

        if (state === "local") {
          const updatedLocalState = localState.map(field =>
            field.id === id
              ? { ...field, value, dirty: true, touched: true }
              : field
          );

          if (!localState.find(field => field.id === id)) {
            updatedLocalState.push({
              id,
              visible: true,
              value,
              dirty: true,
              touched: true
            });
          }

          set({
            localState: updatedLocalState,
            lastSavedAt: new Date().toISOString()
          });
        } else {
          const updatedFinalState = finalState.map(field =>
            field.id === id
              ? { ...field, value, dirty: true, touched: true }
              : field
          );

          if (!finalState.find(field => field.id === id)) {
            updatedFinalState.push({
              id,
              visible: true,
              value,
              dirty: true,
              touched: true
            });
          }

          set({
            finalState: updatedFinalState,
            lastSavedAt: new Date().toISOString()
          });
        }
      },

      setFieldProperty: (
        id: string,
        property: keyof FieldState,
        value: any,
        state: "local" | "final" = "local"
      ) => {
        const { localState, finalState } = get();

        // If setting error property, filter out showError to prevent persistence
        let processedValue = value;
        if (property === "error" && value && typeof value === "object") {
          const { showError, ...errorWithoutShowError } = value;
          processedValue = errorWithoutShowError;
        }

        if (state === "local") {
          const updatedLocalState = localState.map(field =>
            field.id === id ? { ...field, [property]: processedValue } : field
          );
          set({
            localState: updatedLocalState
          });
        } else {
          const updatedFinalState = finalState.map(field =>
            field.id === id ? { ...field, [property]: processedValue } : field
          );
          set({
            finalState: updatedFinalState,
            lastSavedAt: new Date().toISOString()
          });
        }
      },

      hydrateFromData: (data: FieldState[]) => {
        set({
          localState: data,
          finalState: [...data],
          lastSavedAt: new Date().toISOString()
        });
      },

      clearStore: () => {
        set({
          draftId: undefined,
          jobId: undefined,
          jobPlan: undefined,
          lastSavedAt: undefined,
          localState: [],
          finalState: [],
          isDraft: false
        });
      },

      updateLastSavedAt: () => {
        set({ lastSavedAt: new Date().toISOString() });
      },

      setDraftId: (draftId: string) => {
        set({ draftId });
      },
      setJobId: (jobId: string) => {
        set({ jobId });
      },
      setJobPlan: (jobPlan: string) => {
        set({ jobPlan });
      },
      setIsDraft: (isDraft: boolean) => {
        set({ isDraft });
      }
    }),
    {
      name: "temporary-job-posting-storage",
      version: 1
    }
  )
);
